import { blinkConstants } from '@thoughtspot/blink-constants';
import { AppModule, useEmbedContext } from '@thoughtspot/blink-context';
import { isDarkModeInEmbed } from '@thoughtspot/embed-util';
import {
    Content,
    ContentWidth,
    Layout,
    LeftSideBar,
} from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { isRadiantThemeEnabled } from '@thoughtspot/session-service';
import cx from 'classnames';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { useAppContext } from '/@contexts/global-app-context';
import { NavHubContextProvider } from '/@contexts/nav-hub-context';
import { isFullAppEmbed } from '/@utils/embed.util';
import { PERF_EVENT, PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { LeftNavigationHub } from '../components/nav/navigation/left-navigation-hub.container';
import { InitSessionCheckContainer } from '../components/session-status-check/init-session-check.container';
import { ClientStateContextProvider } from '../contexts/global-client-state';
import { setTopNavInternalVariables } from '../theme/top-nav-v3/top-nav-v3-theme-service';
import { useRadiantThemeController } from '../theme/use-radiant-theme-controller';
import { isLeftPanelVisible } from './authenticated-app.util';
import styles from './authenticated-app-layout.module.scss';
import {
    getAppModule,
    getIsOverlayLeftPanel,
    useIsInsightsPage,
} from './routes-util';

export type AuthenticatedAppLayoutProps = {
    isGlobalNavHidden: boolean;
    isPageContentFullHeight: boolean;
    isLoadingIndicatorVisible: boolean;
};

/**
 * Navigation v3 is the only navigation now, with two left-navigation modes:
 * 1. Hub mode, where the left navigation is loaded separately from the
 *    navigation mfe
 * 2. Overlay mode, where the left navigation is loaded separately as an
 *    overlay window
 */
export const AuthenticatedAppLayout: React.FC<AuthenticatedAppLayoutProps> = ({
    isGlobalNavHidden,
    isPageContentFullHeight,
    isLoadingIndicatorVisible,
}) => {
    setPerfLog(PERF_KEYS.AUTHENTICATED_APP_LAYOUT, PERF_EVENT.START);
    const { sessionService, customStylingService } = useAppContext();
    const embedConfig = useEmbedContext();
    const { isEmbedded } = embedConfig;

    const isInsightsPage = useIsInsightsPage();
    const appModule = getAppModule();
    const isOverlayPanel = getIsOverlayLeftPanel();

    const isEverywhereRoute = !!useMatch('/everywhere/*');
    const isSparkflowPinboardRoute = !!useMatch('/sparkflow/pinboard/*');

    // hideNavigationHub incorporates isEverywhereRoute check to show nav hub on everywhere routes
    const hideNavigationHub = useMemo(() => {
        // Always show nav hub on everywhere routes
        if (isEverywhereRoute) {
            return false;
        }

        // v3 only hides OnBoarding (Admin shows the nav hub).
        return appModule === AppModule.OnBoarding;
    }, [isEverywhereRoute, appModule]);

    const radiantEnabled = isRadiantThemeEnabled();
    const themeType = customStylingService.getConfig()?.getThemeType();
    const config = customStylingService.getConfig();
    const leftNavThemeType = useMemo(() => {
        if (themeType === blinkConstants.themeType.CUSTOM && config) {
            const appPanelColor = config.getAppPanelColor(true)?.color;
            if (appPanelColor) {
                const normalizedAppPanelColor = appPanelColor
                    .toUpperCase()
                    .substring(0, 7);
                const normalizedDarkColor = blinkConstants.themeColor.DARK.toUpperCase().substring(
                    0,
                    7,
                );
                if (normalizedAppPanelColor === normalizedDarkColor) {
                    return blinkConstants.themeType.DARK;
                }
            }
        }
        return themeType;
    }, [themeType, config]);

    // Single owner of runtime radiant theming: initial brand/mode apply, the
    // appearance dropdown + OS matchMedia listeners, the admin brand-picker
    // bridge, the embed host's UpdateTheme event, and the nav CSS-variable
    // bridge. Fully inert when the flag is off.
    const { radiantNavMode, effectiveThemeType } = useRadiantThemeController({
        radiantEnabled,
        customStylingService,
        sessionService,
        themeType,
        leftNavThemeType,
        isEmbedded,
        isDarkModeInEmbed: isDarkModeInEmbed(embedConfig),
        useEmbedEventSubscription: embedConfig.useEmbedEventSubscription,
        dispatchValidationError: embedConfig.dispatchValidationError,
    });

    // Set data-theme-type and handle legacy theme service.
    useEffect(() => {
        document
            .getElementById('root')
            ?.setAttribute('data-theme-type', effectiveThemeType);

        // Legacy flow: set top nav internal CSS vars from custom color.
        // Only runs when radiant nav bridge is NOT active.
        if (!radiantNavMode && themeType === blinkConstants.themeType.CUSTOM) {
            setTopNavInternalVariables(
                customStylingService.getConfig()?.getAppPanelColor().color,
            );
        }
    }, [
        themeType,
        leftNavThemeType,
        customStylingService,
        radiantEnabled,
        radiantNavMode,
    ]);

    if (
        !hideNavigationHub &&
        !isPageContentFullHeight &&
        (!isEmbedded || isFullAppEmbed())
    ) {
        return (
            <NavHubContextProvider>
                <Layout isAppLayout>
                    <LeftSideBar
                        className={cx(styles.appLayoutLeftSideBar, {
                            [styles.borderRight]: isLeftPanelVisible(
                                isOverlayPanel,
                                isGlobalNavHidden,
                                isInsightsPage,
                                sessionService,
                                appModule,
                            ),
                            [styles.noBorderRight]: !isLeftPanelVisible(
                                isOverlayPanel,
                                isGlobalNavHidden,
                                isInsightsPage,
                                sessionService,
                                appModule,
                            ),
                        })}
                        isLeftPanelExpanded={isLeftPanelVisible(
                            isOverlayPanel,
                            isGlobalNavHidden,
                            isInsightsPage,
                            sessionService,
                            appModule,
                        )}
                        isLeftPanelFloating={false}
                        leftPanelMinWidth={260}
                    >
                        <LeftNavigationHub
                            themeType={effectiveThemeType}
                            isPrimaryNavHidden={isGlobalNavHidden}
                            isOverlayMode={isOverlayPanel}
                        />
                    </LeftSideBar>
                    <Content
                        contentWidth={ContentWidth.FLUID}
                        noPadding
                        disableOverlayScroll
                        className={styles.content}
                    >
                        <Suspense
                            fallback={
                                <LoadingIndicator.Global
                                    className={styles.loadingIndicator}
                                >
                                    <></>
                                </LoadingIndicator.Global>
                            }
                        >
                            <ClientStateContextProvider>
                                <Suspense
                                    fallback={
                                        <LoadingIndicator.Global
                                            className={styles.loadingIndicator}
                                        >
                                            <></>
                                        </LoadingIndicator.Global>
                                    }
                                >
                                    <Outlet />
                                    {sessionService.isLoggedIn() && (
                                        <InitSessionCheckContainer />
                                    )}
                                </Suspense>
                            </ClientStateContextProvider>
                        </Suspense>
                    </Content>
                </Layout>
            </NavHubContextProvider>
        );
    }

    const hideSparkflowLoader =
        isSparkflowPinboardRoute && !isLoadingIndicatorVisible;

    return (
        <Suspense
            fallback={
                <LoadingIndicator.Global
                    className={styles.loadingIndicator}
                    hideLoader={hideSparkflowLoader}
                >
                    <></>
                </LoadingIndicator.Global>
            }
        >
            <ClientStateContextProvider>
                <Suspense
                    fallback={
                        <LoadingIndicator.Global
                            className={styles.loadingIndicator}
                            hideLoader={hideSparkflowLoader}
                        >
                            <></>
                        </LoadingIndicator.Global>
                    }
                >
                    <Outlet />
                    {sessionService.isLoggedIn() && (
                        <InitSessionCheckContainer />
                    )}
                </Suspense>
            </ClientStateContextProvider>
        </Suspense>
    );
};
